// Feature 08: Link Impact Analysis Page

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import {
  Refresh,
  Add,
  Link as LinkIcon,
  CalendarToday,
} from '@mui/icons-material';
import {
  getPageLinkImpacts,
  recomputeLinkImpact,
} from '../services/linkImpactService';
import LinkImpactBadge, { CorrelationDisclaimer } from '../components/linkImpact/LinkImpactBadge';
import CorrelationChart from '../components/linkImpact/CorrelationChart';
import { LinkImpactData } from '../types/linkImpact.types';

type Engine = 'google' | 'bing';

const LinkImpactPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const queryClient = useQueryClient();

  const [selectedEngine, setSelectedEngine] = useState<Engine>('google');
  const [baselineDays, setBaselineDays] = useState(30);
  const [outcomeDays, setOutcomeDays] = useState(30);

  // Fetch link impacts for the page
  const {
    data: linkImpacts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['linkImpacts', pageId, selectedEngine, baselineDays, outcomeDays],
    queryFn: () =>
      getPageLinkImpacts({
        pageId: pageId!,
        engine: selectedEngine,
        baselineDays,
        outcomeDays,
      }),
    enabled: !!pageId,
    refetchInterval: 120000, // 2 minutes
  });

  // Fetch link events
  // const { data: linkEvents } = useQuery({
  //   queryKey: ['linkEvents', pageId],
  //   queryFn: () => getPageLinkEvents(pageId!),
  //   enabled: !!pageId,
  // });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: (linkEventId: string) =>
      recomputeLinkImpact(linkEventId, {
        engine: selectedEngine,
        baselineDays,
        outcomeDays,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkImpacts', pageId] });
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load link impact data. Please try again later.
      </Alert>
    );
  }

  const hasLinkImpacts = linkImpacts && linkImpacts.length > 0;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Link Impact Attribution</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => {
            // TODO: Open dialog to add new link event
          }}
        >
          Add Link Event
        </Button>
      </Box>

      {/* Disclaimer Banner */}
      <Alert severity="info" icon={<CorrelationDisclaimer />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Supporting Signal - Correlation Analysis:</strong> Link Impact Attribution
          shows statistical correlation between link acquisition timing and performance changes.
          This analysis provides supporting evidence but does not prove causation. Multiple
          factors may contribute to observed performance changes.
        </Typography>
      </Alert>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" gutterBottom>
              Search Engine
            </Typography>
            <ToggleButtonGroup
              value={selectedEngine}
              exclusive
              onChange={(_, value) => value && setSelectedEngine(value)}
              size="small"
              fullWidth
            >
              <ToggleButton value="google">Google</ToggleButton>
              <ToggleButton value="bing">Bing</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Baseline Period</InputLabel>
              <Select
                value={baselineDays}
                label="Baseline Period"
                onChange={(e) => setBaselineDays(e.target.value as number)}
              >
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={60}>60 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Outcome Period</InputLabel>
              <Select
                value={outcomeDays}
                label="Outcome Period"
                onChange={(e) => setOutcomeDays(e.target.value as number)}
              >
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={60}>60 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Link Impact Results */}
      {!hasLinkImpacts ? (
        <Alert severity="info">
          No link events tracked for this page. Add a link event to start tracking impact.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {linkImpacts.map((impact: LinkImpactData) => (
            <Grid item xs={12} key={impact.linkEvent.id}>
              <Card variant="outlined">
                <CardContent>
                  {/* Link Event Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LinkIcon color="primary" />
                        <Typography variant="h6">
                          Link from {new URL(impact.linkEvent.sourceUrl).hostname}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1} alignItems="center">
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Acquired: {new Date(impact.linkEvent.acquisitionDate).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={`${impact.timeLagDays} days lag`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Box display="flex" gap={1} alignItems="center">
                      <LinkImpactBadge
                        level={impact.correlationScore.level}
                        score={impact.correlationScore.value}
                      />
                      <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => recomputeMutation.mutate(impact.linkEvent.id)}
                        disabled={recomputeMutation.isPending}
                      >
                        Recompute
                      </Button>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Correlation Analysis */}
                  <CorrelationChart
                    score={impact.correlationScore}
                    metricChanges={impact.metricChanges}
                  />

                  {/* Analysis Notes */}
                  {impact.linkEvent.notes && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Notes:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {impact.linkEvent.notes}
                      </Typography>
                    </Box>
                  )}

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      Computed: {new Date(impact.computedAt).toLocaleString()} •
                      Baseline: {impact.baselinePeriodDays} days before •
                      Outcome: {impact.outcomePeriodDays} days after
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default LinkImpactPage;
